import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Pantallas } from '../services/api';
import { Modal, LabeledInput, LabeledSelect, cls } from './ui';
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://tvs-api.chatup.pe';

export default function PlaylistEditor({ group, items, setItems, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ url: '', url_hls: '', titulo: '', status: 'activo' });
  const [busy, setBusy] = useState(false);

  // Upload local
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [progress, setProgress] = useState(0);

  const sorted = useMemo(
    () => [...(items || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [items]
  );

  const up = (idx) => {
    if (idx <= 0) return;
    const copy = [...sorted];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    const normalized = copy.map((it, i) => ({ ...it, orden: i }));
    setItems(normalized);
  };

  const down = (idx) => {
    if (idx >= sorted.length - 1) return;
    const copy = [...sorted];
    [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
    const normalized = copy.map((it, i) => ({ ...it, orden: i }));
    setItems(normalized);
  };

  const commitReorder = async () => {
    try {
      setBusy(true);
      const updates = sorted.map((it, i) => ({ id: it.id, orden: i }));
      await Pantallas.reorderPlaylist(group._id, updates);
      await onRefresh();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (videoId) => {
    if (!window.confirm('¿Eliminar este video de la playlist?')) return;
    try {
      setBusy(true);
      await Pantallas.deletePlaylistItem(group._id, videoId);
      await onRefresh();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadVideo = async () => {
    if (!uploadFile) {
      alert('Selecciona un archivo de video');
      return;
    }
    try {
      setUploading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append('file', uploadFile);

      // Subida con progreso
      const { data } = await axios.post(`${API_BASE}/api/media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const percent = Math.round((evt.loaded * 100) / evt.total);
            setProgress(percent);
          }
        },
      });

      setNewItem((v) => ({
        ...v,
        url: data.url_mp4 || data.url || '',
        url_hls: data.url_hls || '',
        titulo: v.titulo || uploadFile.name,
        status: v.status || 'activo',
      }));

      alert('Video subido correctamente');
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setUploading(false);
      setProgress(0);
      try {
        if (document.activeElement) document.activeElement.value = '';
      } catch {}
    }
  };

  const addItems = async () => {
    if (!newItem.url_hls) {
      alert('Sube un archivo de video (HLS .m3u8) antes de agregarlo a la playlist');
      return;
    }
    try {
      setBusy(true);
      const nextOrden = sorted.length;

      const payloadItem = {
        ...newItem,
        orden: nextOrden,
        url_hls: newItem.url_hls,
        url: newItem.url || '',
      };

      await Pantallas.addPlaylistItems(group._id, [payloadItem]);

      setCreating(false);
      setNewItem({ url: '', url_hls: '', titulo: '', status: 'activo' });
      setUploadFile(null);
      await onRefresh();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-white/70">{sorted.length} elementos en la playlist</div>
        <div className="flex gap-2">
          <button
            onClick={() => setCreating(true)}
            className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm font-medium"
          >
            Agregar video
          </button>
          <button
            disabled={busy}
            onClick={commitReorder}
            className={cls(
              'rounded-xl bg-slate-700 hover:bg-slate-600 px-3 py-2 text-sm font-medium',
              busy && 'opacity-60 cursor-not-allowed'
            )}
          >
            Guardar orden
          </button>
        </div>
      </div>

      {/* Listado de videos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((it, idx) => (
          <div
            key={it.id}
            className="group relative rounded-2xl border border-white/10 overflow-hidden bg-slate-800/50"
          >
            <div className="absolute left-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 border border-white/10 text-sm">
              {idx + 1}
            </div>
            <div className="aspect-video bg-gradient-to-b from-slate-700/40 to-slate-900" />
            <div className="p-3 grid gap-2">
              <div className="font-medium truncate" title={it.titulo || it.url_hls || it.url}>
                {it.titulo || it.url_hls || it.url}
              </div>
              <div className="text-xs text-white/60 break-all line-clamp-1">
                {it.url_hls ? (
                  <>
                    HLS: <code className="text-green-400">{it.url_hls}</code>
                    {it.url ? (
                      <>
                        • MP4: <code className="text-white/80">{it.url}</code>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>{it.url}</>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => up(idx)}
                    className="rounded-lg bg-white/10 px-2 py-1 text-xs hover:bg-white/20"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => down(idx)}
                    className="rounded-lg bg-white/10 px-2 py-1 text-xs hover:bg-white/20"
                  >
                    ↓
                  </button>
                </div>
                <button
                  onClick={() => removeItem(it.id)}
                  className="rounded-lg bg-red-600/80 hover:bg-red-600 px-2 py-1 text-xs"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Agregar video a playlist"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setCreating(false)}
              className="rounded-xl px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600"
            >
              Cancelar
            </button>

            <button
              onClick={addItems}
              disabled={uploading}
              className={`rounded-xl px-3 py-2 text-sm 
                          ${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500'}`}
            >
              {uploading ? 'Subiendo...' : 'Agregar'}
            </button>
          </div>
        }
      >
        <div className="grid gap-3">
          <LabeledInput
            label="Título (opcional)"
            value={newItem.titulo}
            onChange={(e) => setNewItem((v) => ({ ...v, titulo: e.target.value }))}
          />

          {/* Upload */}
          <label className="grid gap-1">
            <span className="text-sm text-white/70">Archivo de video</span>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2"
            />
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  type="button"
                  onClick={uploadVideo}
                  disabled={uploading || !uploadFile}
                  className={cls(
                    'rounded-xl px-3 py-2 text-sm',
                    uploading || !uploadFile
                      ? 'bg-slate-700 opacity-60 cursor-not-allowed'
                      : 'bg-slate-700 hover:bg-slate-600'
                  )}
                >
                  {uploading
                    ? `Subiendo... ${progress > 0 ? progress + '%' : ''}`
                    : 'Subir video'}
                </button>

                {newItem.url || newItem.url_hls ? (
                  <span className="text-xs text-white/60">
                    {newItem.url && (
                      <>
                        MP4: <code className="text-white/80">{newItem.url}</code>
                      </>
                    )}
                    {newItem.url_hls && (
                      <>
                        {newItem.url ? ' • ' : ''}HLS:{' '}
                        <code className="text-green-400">{newItem.url_hls}</code>
                      </>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-white/50">Sin archivo todavía</span>
                )}
              </div>

              {/* Barra de progreso */}
              {uploading && (
                <div className="w-full h-2 bg-slate-700 rounded overflow-hidden">
                  <div
                    className="h-2 bg-indigo-500 transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>

            <p className="text-xs text-white/50">
              Al subir, el servidor genera HLS y devuelve <code>url_hls</code> (playlist .m3u8).
              La pantalla usa HLS, MP4 queda como respaldo.
            </p>
          </label>

          <LabeledSelect
            label="Estado"
            value={newItem.status}
            onChange={(e) => setNewItem((v) => ({ ...v, status: e.target.value }))}
          >
            <option value="activo">activo</option>
            <option value="inactivo">inactivo</option>
          </LabeledSelect>
        </div>
      </Modal>
    </div>
  );
}
